"use client";

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { RiCheckboxCircleLine, RiShoppingBag3Line, RiTimeLine } from "react-icons/ri";
import { Button, ChipGroup, EmptyState, Skeleton } from "@dionatanbig2be/big2be-ui";
import { toast } from "@/shared/components/toast";
import { OrdersSection } from "./OrdersSection";
import { OrderDetailsDrawer } from "./OrderDetailsDrawer";
import { useOrders } from "../../hooks/useOrders";
import { useRepeatOrder } from "../../hooks/useRepeatOrder";
import type { OrderCard, OrderStatus } from "../../types/orders";

interface MyOrdersPanelProps {
    /**
     * Mostra só os N pedidos mais recentes, numa lista única — sem filtros
     * nem "carregar mais". É o modo do "Comprar de novo" do header.
     */
    limit?: number;
    /**
     * Onde mora o pedido aberto: na URL (`?pedido=`, padrão de meus pedidos)
     * ou só em memória — fora da página da conta o drawer não deve sujar a
     * URL da tela em que a pessoa está.
     */
    orderState?: "url" | "local";
    /** Chamado quando "Pedir novamente" de fato pôs itens no carrinho. */
    onRepeated?: () => void;
}

type OrdersFilter = "todos" | "andamento" | "concluidos" | "cancelados";

const FILTERS: { id: OrdersFilter; label: string }[] = [
    { id: "todos", label: "Todos" },
    { id: "andamento", label: "Em andamento" },
    { id: "concluidos", label: "Concluídos" },
    { id: "cancelados", label: "Cancelados" },
];

const IN_PROGRESS: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
    "aguardando-pagamento",
    "enviado",
]);

function matchesFilter(order: OrderCard, filter: OrdersFilter): boolean {
    switch (filter) {
        case "andamento":
            return IN_PROGRESS.has(order.status);
        case "concluidos":
            return order.status === "concluido";
        case "cancelados":
            return order.status === "cancelado";
        default:
            return true;
    }
}

/**
 * Meus pedidos — lista com filtros por status e detalhes num drawer lateral
 * (mesmo shell do carrinho). O pedido aberto vive na URL (`?pedido=`), então
 * F5 mantém o drawer aberto e "Acompanhar pedido" pode apontar direto para um
 * pedido. "Pedir novamente" manda os itens ao carrinho e abre o drawer do
 * carrinho para a pessoa já seguir para o checkout.
 */
export function MyOrdersPanel({ limit, orderState = "url", onRepeated }: MyOrdersPanelProps = {}) {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [localOrderId, setLocalOrderId] = useState<string | null>(null);
    const orderParam = orderState === "url" ? searchParams.get("pedido") : localOrderId;

    const { cards: loaded, status, hasMore, loadingMore, loadMore, reload, getDetails } = useOrders(
        limit ? { pageSize: limit } : undefined
    );
    // A página pode vir maior que o pedido (back ignora o `limit`): corta aqui.
    const cards = useMemo(() => (limit ? loaded.slice(0, limit) : loaded), [loaded, limit]);
    const { repeat, repeating } = useRepeatOrder();
    const [filter, setFilter] = useState<OrdersFilter>("todos");
    /** Pedido do drawer — pode ser o card magro da lista enquanto os detalhes chegam. */
    const [current, setCurrent] = useState<OrderCard | null>(null);

    // O drawer abre quando a URL aponta para o pedido carregado; fechar limpa
    // a URL e o `current` fica para a animação de saída.
    const drawerOpen = Boolean(orderParam) && current?.id === orderParam;

    function setOrderParam(orderId: string | null) {
        if (orderState === "local") {
            setLocalOrderId(orderId);
            return;
        }
        const params = new URLSearchParams(searchParams.toString());
        if (orderId) params.set("pedido", orderId);
        else params.delete("pedido");
        const qs = params.toString();
        router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    }

    // A URL aponta para um pedido que ainda não é o `current` mas já está na
    // lista → mostra o card magro imediatamente. Ajuste durante o render (sem
    // effect), guardado pelo id — para no primeiro acerto.
    if (orderParam && current?.id !== orderParam) {
        const thin = cards.find((c) => c.id === orderParam);
        if (thin) setCurrent(thin);
    }

    // Completa o `?pedido=` da URL com os detalhes — o cache do hook torna
    // reaberturas instantâneas (e cobre deep-link de pedido fora da lista).
    useEffect(() => {
        if (!orderParam) return;
        if (current?.id === orderParam && current.hydrated) return;

        let active = true;
        getDetails(orderParam)
            .then((full) => {
                if (active) setCurrent(full);
            })
            .catch(() => {
                if (!active) return;
                toast.error("Não foi possível carregar os detalhes do pedido.");
                setOrderParam(null);
            });
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- setOrderParam muda a cada render (deriva de searchParams); as deps que importam são estas
    }, [orderParam, current, getDetails]);

    const counts = useMemo(
        () =>
            FILTERS.reduce<Record<OrdersFilter, number>>(
                (acc, f) => {
                    acc[f.id] = cards.filter((c) => matchesFilter(c, f.id)).length;
                    return acc;
                },
                { todos: 0, andamento: 0, concluidos: 0, cancelados: 0 }
            ),
        [cards]
    );

    function openOrder(order: OrderCard) {
        setCurrent(order); // abre já com o resumo; o effect completa com os detalhes
        setOrderParam(order.id);
    }

    async function handleRepeat(order: OrderCard) {
        // O fluxo (validação na loja ativa, refresh, gaveta do carrinho,
        // toasts) é o de `useRepeatOrder` — compartilhado com o header. Aqui
        // só fechamos o drawer do pedido quando algo de fato entrou.
        const result = await repeat(order.id);
        if (result && result.addedCount > 0) {
            setOrderParam(null);
            onRepeated?.();
        }
    }

    async function handleLoadMore() {
        try {
            await loadMore();
        } catch {
            toast.error("Não foi possível carregar mais pedidos.");
        }
    }

    if (status === "loading") return <OrdersSkeleton withFilters={!limit} />;

    if (status === "error") {
        return (
            <div className="flex flex-col items-center gap-3 py-12">
                <p className="text-sm text-base-content/50">
                    Não foi possível carregar seus pedidos.
                </p>
                <Button type="button" variant="outline" size="sm" onClick={reload}>
                    Tentar novamente
                </Button>
            </div>
        );
    }

    if (cards.length === 0) {
        return (
            <EmptyState
                className="rounded-2xl border border-dashed border-base-300/80 px-8 py-14"
                icon={<RiShoppingBag3Line size={36} aria-hidden="true" />}
                title="Você ainda não fez nenhum pedido."
                description="Quando você comprar, seus pedidos aparecem aqui para acompanhar tudo de perto."
                action={
                    // Rota de navegação: fica como <Link> (o Button da lib só renderiza <button>).
                    <Link href="/" className="btn btn-primary btn-sm">
                        Explorar produtos
                    </Link>
                }
            />
        );
    }

    const detailsDrawer = (
        <OrderDetailsDrawer
            order={current}
            open={drawerOpen}
            onClose={() => setOrderParam(null)}
            onRepeat={(order) => void handleRepeat(order)}
            repeating={repeating}
        />
    );

    if (limit) {
        return (
            <div className="flex flex-col gap-6">
                <OrdersSection
                    title="Últimas compras"
                    orders={cards}
                    onSelectOrder={openOrder}
                    emptyIcon={<RiShoppingBag3Line size={32} aria-hidden="true" />}
                    emptyTitle="Nenhuma compra ainda."
                />
                {/* Este modo vive dentro de outro drawer (header): o painel
                    dele anima com transform, o que prenderia o `fixed` do
                    drawer de detalhes à caixa do pai. No body ele cobre a tela
                    e, por vir depois no DOM, abre por cima da lista. */}
                {typeof document !== "undefined" && createPortal(detailsDrawer, document.body)}
            </div>
        );
    }

    const inProgress = cards.filter((c) => matchesFilter(c, "andamento"));
    const finished = cards.filter((c) => !IN_PROGRESS.has(c.status));
    const filtered = cards.filter((c) => matchesFilter(c, filter));

    return (
        <div className="flex flex-col gap-6">
            <ChipGroup
                name="orders-filter"
                label="Filtrar pedidos por status"
                mode="radio"
                size="sm"
                selectedIds={[filter]}
                onSelect={(id) => setFilter(id as OrdersFilter)}
                options={FILTERS.map((f) => ({
                    id: f.id,
                    label: `${f.label} (${counts[f.id]})`,
                    disabled: f.id !== "todos" && counts[f.id] === 0,
                }))}
                // Mobile (sem espaço p/ 4 chips): trilho horizontal rolável, sem
                // scrollbar. Com container largo (painel desktop) volta a quebrar
                // linha normalmente. Corte por container, como o grid dos cards.
                itemsClassName="flex flex-nowrap gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden @[44rem]:flex-wrap @[44rem]:overflow-visible"
            />

            {filter === "todos" ? (
                <>
                    <OrdersSection
                        title="Em andamento"
                        orders={inProgress}
                        onSelectOrder={openOrder}
                        emptyIcon={<RiTimeLine size={32} aria-hidden="true" />}
                        emptyTitle="Nenhum pedido em andamento."
                    />
                    <OrdersSection
                        title="Finalizados"
                        orders={finished}
                        onSelectOrder={openOrder}
                        emptyIcon={<RiCheckboxCircleLine size={32} aria-hidden="true" />}
                        emptyTitle="Nenhum pedido finalizado ainda."
                    />
                </>
            ) : (
                <OrdersSection
                    title={FILTERS.find((f) => f.id === filter)?.label ?? ""}
                    orders={filtered}
                    onSelectOrder={openOrder}
                    emptyIcon={<RiShoppingBag3Line size={32} aria-hidden="true" />}
                    emptyTitle="Nenhum pedido neste status."
                />
            )}

            {hasMore && (
                <Button
                    type="button"
                    variant="outline"
                    size="md"
                    isLoading={loadingMore}
                    disabled={loadingMore}
                    onClick={() => void handleLoadMore()}
                    className="self-center"
                >
                    {loadingMore ? "Carregando…" : "Carregar mais pedidos"}
                </Button>
            )}

            {detailsDrawer}
        </div>
    );
}

function OrdersSkeleton({ withFilters }: { withFilters: boolean }) {
    return (
        <div className="flex flex-col gap-5" aria-busy="true" aria-label="Carregando pedidos">
            {withFilters && (
                <div className="flex gap-1.5">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-7 w-24 rounded-md" />
                    ))}
                </div>
            )}
            <Skeleton className="h-4 w-32" />
            <div className="grid grid-cols-1 gap-4 @[44rem]:grid-cols-2">
                {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-52 w-full rounded-2xl" />
                ))}
            </div>
        </div>
    );
}
